import { keyBy } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { RateCardLite } from 'api/rate-cards';
import { PageLoading } from 'components/atoms/loading';
import { Page } from 'components/molecules/page';
import { Caption01 } from 'components/styles';
import { PermissionsContext } from 'contexts/permissions';
import { GranularPermissions, useHasAccess } from 'hooks/use-has-access';
import { useTranslation } from 'hooks/use-translation';
import { useAccountProfile } from 'queries/account';
import { useRateCardsQuery } from 'queries/rate-cards';
import { RateCardsDrawer } from './crud';
import { RateCardsEmptyState } from './empty-state';
import { RateCardsSection } from './list';

const PageTitleWrapper = styled(Page.Header.Title)`
  display: flex !important;
  flex-direction: column;
  align-items: flex-start !important;
  justify-content: flex-start;
  flex: 1;
  height: auto;
`;
const PageDescription = styled.div`
  ${Caption01};
  color: var(--gray-80);
  padding: var(--spacing-02) 0 var(--spacing-04);
`;
const PageContent = styled(Page.Content)`
  padding: var(--layout-01);
`;

export const RateCardsPage: FC = () => {
  const title = useTranslation('settings.rate_cards.title');
  const description = useTranslation('settings.rate_cards.description');
  const documentTitle = useTranslation('settings.rate_cards.title');

  const { data: account } = useAccountProfile();
  const { data: rateCards, isLoading: isRateCardsLoading } =
    useRateCardsQuery();

  const { hasAccess: canCreate } = useHasAccess({
    permissions: ['CREATE'],
    resource: 'rateCard',
  });
  const { hasAccess: canWrite } = useHasAccess({
    permissions: ['WRITE'],
    resource: 'rateCard',
  });
  const { hasAccess: canDelete } = useHasAccess({
    permissions: ['DELETE'],
    resource: 'rateCard',
  });

  const rateCardPermissions: GranularPermissions = useMemo(
    () => ({
      canCreate,
      canWrite,
      canDelete,
    }),
    [canCreate, canDelete, canWrite],
  );

  const rateCardsLookup = useMemo(() => {
    if (isRateCardsLoading) {
      return undefined;
    }

    return keyBy(rateCards, 'rateCardId');
  }, [isRateCardsLoading, rateCards]);

  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedRateCard, setSelectedRateCard] =
    useState<RateCardLite | undefined>(undefined);
  const [isNewRateCardVersion, setIsNewRateCardVersion] = useState(false);

  const openRateCardDrawer = useCallback(
    (rateCardId: number | undefined) => {
      setIsDrawerVisible(true);
      setSelectedRateCard(
        rateCardId ? rateCardsLookup?.[rateCardId] : undefined,
      );
    },
    [rateCardsLookup],
  );

  const createNewRateCardVersion = useCallback(
    (rateCardId: number) => {
      openRateCardDrawer(rateCardId);
      setIsNewRateCardVersion(true);
    },
    [openRateCardDrawer],
  );

  const closeRateCardDrawer = useCallback(() => {
    setIsDrawerVisible(false);
    setSelectedRateCard(undefined);
    setIsNewRateCardVersion(false);
  }, []);

  return (
    <Page>
      <Helmet>{documentTitle}</Helmet>
      <Page.Header
        $white
        title={
          <PageTitleWrapper>
            {title}
            <PageDescription>{description}</PageDescription>
          </PageTitleWrapper>
        }
      />
      {isRateCardsLoading || rateCards == null || account == null ? (
        <PageLoading css />
      ) : (
        <PermissionsContext.Provider value={rateCardPermissions}>
          <PageContent>
            {rateCards.length === 0 ? (
              <RateCardsEmptyState
                primaryCta={() => {
                  openRateCardDrawer(undefined);
                }}
              />
            ) : (
              <RateCardsSection
                rateCards={rateCards}
                openRateCardDrawer={openRateCardDrawer}
                createNewRateCardVersion={createNewRateCardVersion}
              />
            )}
            <RateCardsDrawer
              isVisible={isDrawerVisible}
              account={account}
              selectedRateCard={selectedRateCard}
              isNewRateCardVersion={isNewRateCardVersion}
              updateNewRateCardVersion={setIsNewRateCardVersion}
              onClose={closeRateCardDrawer}
            />
          </PageContent>
        </PermissionsContext.Provider>
      )}
    </Page>
  );
};
